import { useCallback } from "react";
import type { Location } from "history";
import { noop } from "lodash";
import { useHotkeys } from "react-hotkeys-hook";
import { useLocation } from "react-router-dom";
import type { Post } from "@circle-react/types";
import { useNavigateToPost } from "./useNavigateToPost";
import { useNavigationState } from "./useNavigationState";
import { usePostFetching } from "./usePostFetching";

export interface LocationState {
  hasNextPage?: boolean;
  params?: string | Record<string, unknown>;
  postList?: Post[];
  prevLocation?: Location;
}

export interface NavigationProps {
  currentPost: any;
  initialPage?: number;
  initialPostList?: Post[];
  isLoading?: boolean;
  isNavigatingInsidePostModal?: boolean;
  isWithinProfileContext?: boolean;
  params?: Record<string, unknown>;
  prevLocation?: Location<LocationState>;
  profileContextMember?: {
    id: number;
    public_uid: string;
  };
  setIsNavigatingInsidePostModal?: (value: boolean) => void;
  wasInFeed?: boolean;
}

interface Page {
  count: number;
  has_next_page: boolean;
  page: number;
  page_count: number;
  per_page: number;
  records: Post[];
}

type PageParam = number | null;

export interface FetchedData {
  pageParams: PageParam;
  pages: Page[];
}

export const useNavigationLogic = ({
  currentPost,
  prevLocation,
  params,
  isNavigatingInsidePostModal,
  setIsNavigatingInsidePostModal,
  wasInFeed = false,
  isWithinProfileContext,
  profileContextMember,
}: NavigationProps) => {
  const { navigateToPost } = useNavigateToPost();
  const location = useLocation<LocationState>();

  const {
    direction,
    setDirection,
    isPostQueryEnabled,
    setIsPostQueryEnabled,
    hasNextPage,
    setHasNextPage,
  } = useNavigationState(location.state);

  const handleSuccess = useCallback(
    (fetchedData: FetchedData) => {
      if (!isNavigatingInsidePostModal) return;
      const lastPage = fetchedData.pages[fetchedData.pages.length - 1];

      if (lastPage && lastPage.records.length > 0) {
        setIsPostQueryEnabled(false);
        setHasNextPage(lastPage.has_next_page);
        const currentPostIndex = lastPage.records.findIndex(
          (post: Post) => post.id === currentPost.id,
        );

        const nextPostIndex =
          currentPostIndex !== -1 &&
          currentPostIndex < lastPage.records.length - 1
            ? currentPostIndex + 1
            : 0;

        navigateToPost({
          newPost: lastPage.records[nextPostIndex],
          prevLocation,
          params,
          isWithinProfileContext,
          profileContextMember,
        });
      }
      setIsNavigatingInsidePostModal?.(false);
    },
    [
      isNavigatingInsidePostModal,
      navigateToPost,
      setIsNavigatingInsidePostModal,
      setHasNextPage,
      setIsPostQueryEnabled,
      currentPost,
      params,
      prevLocation,
      isWithinProfileContext,
      profileContextMember,
    ],
  );

  const { isLoading, posts } = usePostFetching(
    isPostQueryEnabled,
    wasInFeed,
    handleSuccess,
    params,
    isWithinProfileContext,
    profileContextMember,
  );

  const handleNavigation = useCallback(
    (navDirection: "next" | "previous") => {
      if (isLoading || !posts || !currentPost) return;

      setIsNavigatingInsidePostModal?.(true);
      setDirection(navDirection);

      const currentIndex = posts.findIndex(post => post.id === currentPost.id);
      if (currentIndex === -1) return;

      const newIndex =
        navDirection === "next" ? currentIndex + 1 : currentIndex - 1;

      if (newIndex < 0 || newIndex >= posts.length) {
        if (navDirection === "next") {
          setIsPostQueryEnabled(true);
        } else {
          setIsNavigatingInsidePostModal?.(false);
        }
        return;
      }

      navigateToPost({
        newPost: posts[newIndex],
        prevLocation,
        params,
        isWithinProfileContext,
        profileContextMember,
      });
      setIsNavigatingInsidePostModal?.(false);
    },
    [
      isLoading,
      posts,
      currentPost,
      setDirection,
      navigateToPost,
      setIsPostQueryEnabled,
      prevLocation,
      params,
      setIsNavigatingInsidePostModal,
      isWithinProfileContext,
      profileContextMember,
    ],
  );

  const handlePostNavigation = useCallback(
    (direction: "next" | "previous") => {
      if (!isLoading && !isNavigatingInsidePostModal) {
        handleNavigation(direction);
      }
    },
    [handleNavigation, isLoading, isNavigatingInsidePostModal],
  );

  const isFirstItem = useCallback(() => {
    if (!posts || !currentPost) return false;

    return posts.findIndex(post => post.id === currentPost.id) === 0;
  }, [posts, currentPost]);

  const isLastItemInLastPage = useCallback(() => {
    if (!posts || !currentPost) return false;

    const isLastItem =
      posts.findIndex(post => post.id === currentPost.id) === posts.length - 1;

    return isLastItem && !hasNextPage;
  }, [posts, currentPost, hasNextPage]);

  useHotkeys(
    "right",
    () => {
      if (!isLastItemInLastPage()) {
        handlePostNavigation("next");
      }
    },
    { enabled: !isLoading && !isNavigatingInsidePostModal },
    [handlePostNavigation, isLoading, isNavigatingInsidePostModal],
  );
  useHotkeys(
    "left",
    () => {
      if (!isFirstItem()) {
        handlePostNavigation("previous");
      }
    },
    { enabled: !isLoading && !isNavigatingInsidePostModal },
    [handlePostNavigation, isLoading, isNavigatingInsidePostModal, isFirstItem],
  );

  return {
    isNavigatingToNextPost: direction === "next",
    isNavigatingToPreviousPost: direction === "previous",
    isLoading,
    setIsNavigatingInsidePostModal,
    isNavigatingInsidePostModal,
    shouldDisableLeftNavigation:
      isLoading || isNavigatingInsidePostModal || isFirstItem(),
    shouldDisableRightNavigation:
      isLoading || isNavigatingInsidePostModal || isLastItemInLastPage(),
    handleNextPost: () =>
      isLastItemInLastPage() ? noop : handlePostNavigation("next"),
    handlePreviousPost: () =>
      isFirstItem() ? noop : handlePostNavigation("previous"),
  };
};
